import React from 'react';
import loadable from '@loadable/component';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';
import { parse } from '../util/urlHelpers';
import { seoLandingRoutePath } from '../util/seoLandings';
import { landingCategoryRoutePath } from '../util/landingCategories';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const EmailFirstLoginPage = loadable(() => import(/* webpackChunkName: "EmailFirstLoginPage" */ '../containers/EmailFirstLoginPage/EmailFirstLoginPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const CMSPage = loadable(() => import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const BillingDetailsPage = loadable(() => import(/* webpackChunkName: "BillingDetailsPage" */ '../containers/BillingDetailsPage/BillingDetailsPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const EditListingTabShortcutPage = loadable(() => import(/* webpackChunkName: "EditListingTabShortcutPage" */ '../containers/EditListingTabShortcutPage/EditListingTabShortcutPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const CostCalculatorPage = loadable(() => import(/* webpackChunkName: "CostCalculationPage" */ '../containers/CostCalculationPage/CostCalculationPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'));
const ListingPageCoverPhoto = loadable(() => import(/* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'));
const ListingPageCarousel = loadable(() => import(/* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const NewListingPage = loadable(() => import(/* webpackChunkName: "NewListingPage" */ '../containers/NewListingPage/NewListingPage'));
const FavouriteListingsPage = loadable(() => import(/* webpackChunkName: "FavouriteListingsPage" */ '../containers/FavouriteListingsPage/FavouriteListingsPage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
const SearchPageWithMap = loadable(() => import(/* webpackChunkName: "SearchPageWithMap" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithMap'));
const SearchPageWithGrid = loadable(() => import(/* webpackChunkName: "SearchPageWithGrid" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithGrid'));
const StripePayoutPage = loadable(() => import(/* webpackChunkName: "StripePayoutPage" */ '../containers/StripePayoutPage/StripePayoutPage'));
const SeoLandingPage = loadable(() => import(/* webpackChunkName: "SeoLandingPage" */ '../containers/SeoLandingPage/SeoLandingPage'));
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));

const HoldListingsPage = loadable(() => import(/* webpackChunkName: "OnholdListingsPage" */ '../containers/OnholdListingPage/OnholdListingsPage'));
const UpcomingListingsPage = loadable(() => import(/* webpackChunkName: "UpcomingListingsPage" */ '../containers/UpcomingListingsPage/UpcomingListingsPage'));
const PastListingsPage = loadable(() => import(/* webpackChunkName: "PastListingsPage" */ '../containers/PastListingsPage/PastListingsPage'));
const ReservaAntiguesPage = loadable(() => import(/* webpackChunkName: "ReservaAntiguesPage" */ '../containers/ReservaAntiguesPage/ReservaAntiguesPage'));
const IngresosPage = loadable(() => import(/* webpackChunkName: "IngresosPage" */ '../containers/IngresosPage/IngresosPage'));
const DashboardPage = loadable(() => import(/* webpackChunkName: "DashboardPage" */ '../containers/DashboardPage/DashboardPage'));
const ExternalBookingPaymentPage = loadable(() => import(/* webpackChunkName: "ExternalBookingPaymentPage" */ '../containers/ExternalBookingPaymentPage/ExternalBookingPaymentPage'));

//Pages from CMS

const BarbacoaPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/Barbacoa/BarbacoaPage'));

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));

export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'BillingDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
];

export const RESERVATIONS_SETTINGS_PAGE = [
  'HoldListingsPage',
  'UpcomingListingsPage',
  'PastLisingsPage',
  'ReservaAntiguesPage',
  'IngresosPage',
];

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// The standalone /login and /signup pages are deprecated: authentication now
// happens through the access modal. Visiting those URLs sends the user to the
// landing page (cocopool.es) with the access modal open (?access=open).
//
// A `from` deep-link (carried in the query by the auth gate in Routes.js, or in
// history state on a client-side redirect) is forwarded as a `from` query param
// so the modal can return the user to the page they originally requested.
const RedirectToAccessModal = props => {
  const params = props?.location?.search ? parse(props.location.search) : {};
  const from =
    (typeof params.from === 'string' && params.from) || props?.location?.state?.from || null;
  const search = from ? `?access=open&from=${encodeURIComponent(from)}` : '?access=open';
  // Keep `from` in history state too: Topbar post-login for hosts reads state.from
  // (closing the modal strips `from` from the query).
  return (
    <NamedRedirect name="LandingPage" search={search} state={from ? { from } : {}} />
  );
};

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig) => {
  const SearchPage = layoutConfig.searchPage?.variantType === 'map'
    ? SearchPageWithMap
    : SearchPageWithGrid;
  const ListingPage = layoutConfig.listingPage?.variantType === 'carousel'
    ? ListingPageCarousel
    : ListingPageCoverPhoto;

  return [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    // La home con una categoría del hero ya marcada (/piscinas, /jardines, /interiores).
    {
      path: landingCategoryRoutePath(),
      name: 'LandingCategoryPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    {
      path: '/s/:tab',
      name: 'SearchBasePage',
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/s/jardines',
      name: 'SearchGardenPage',
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/s',
      name: 'SearchPage',
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    // Must be registered before `/l/:slug/:id` so `edit`/`:tab` are not
    // treated as a listing slug and id. Allowed tabs: typeofspace, location,
    // capacity, details, pricing, availability, photos.
    {
      path: '/l/edit/:tab',
      name: 'EditListingTabShortcutPage',
      auth: true,
      authPage: 'LoginPage',
      component: EditListingTabShortcutPage,
      loadData: pageDataLoadingAPI.EditListingTabShortcutPage.loadData,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      component: NewListingPage,
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: RedirectToAccessModal,
    },
    {
      path: '/login/email',
      name: 'EmailFirstLoginPage',
      component: EmailFirstLoginPage,
      loadData: pageDataLoadingAPI.EmailFirstLoginPage.loadData,
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: RedirectToAccessModal,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/calculadora-ingresos-alquiler-piscinas',
      name: 'CostCalculatorPage',
      auth: false,
      authPage: 'LoginPage',
      component: CostCalculatorPage,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: pageDataLoadingAPI.TransactionPage.loadData,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageListingsPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      path: '/favourite-listings',
      name: 'FavouriteListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: FavouriteListingsPage,
      loadData: pageDataLoadingAPI.FavouriteListingsPage.loadData,
    },
    // {
    //   path: '/reservas-antiguas',
    //   name: 'ReservaAntiguesPage',
    //   auth: true,
    //   authPage: 'LoginPage',
    //   component: ReservaAntiguesPage,
    //   loadData: pageDataLoadingAPI.ReservaAntiguesPage.loadData,
    // },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      path: '/account/billing-details',
      name: 'BillingDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: BillingDetailsPage,
      loadData: pageDataLoadingAPI.BillingDetailsPage.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      path: '/account/payments',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData,
    },

    {
      path: '/reservations',
      name: 'ReservationsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="UpcomingListingsPage" params={{ tab: 'sales' }} />,
    },
    {
      path: '/reservations/onhold-reservations',
      name: 'HoldListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: HoldListingsPage,
      loadData: pageDataLoadingAPI.OnholdListingsPage.loadData,
    },
    {
      path: '/reservations/upcoming-reservations',
      name: 'UpcomingListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: UpcomingListingsPage,
      loadData: pageDataLoadingAPI.UpcomingListingsPage.loadData,
    },
    {
      path: '/reservations/past-reservations',
      name: 'PastListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PastListingsPage,
      loadData: pageDataLoadingAPI.PastListingsPage.loadData,
    },
    {
      path: '/reservations/reservas-antiguas',
      name: 'ReservaAntiguesPage',
      auth: true,
      authPage: 'LoginPage',
      component: ReservaAntiguesPage,
      loadData: pageDataLoadingAPI.ReservaAntiguesPage.loadData,
    },
    {
      path: '/ingresos',
      name: 'IngresosPage',
      auth: true,
      authPage: 'LoginPage',
      component: IngresosPage,
      loadData: pageDataLoadingAPI.IngresosPage.loadData,
    },
    {
      path: '/dashboard',
      name: 'DashboardPage',
      auth: true,
      authPage: 'LoginPage',
      component: DashboardPage,
      loadData: pageDataLoadingAPI.DashboardPage.loadData,
    },

    // Guest payment page for an external ("off-platform") booking. NO `auth`: the
    // bañista may have no Cocopool account, so the opaque token in the URL is the
    // only credential and the server returns a redacted view of the booking.
    // See docs/external-bookings.md.
    {
      path: '/reserva-externa/:token',
      name: 'ExternalBookingPaymentPage',
      component: ExternalBookingPaymentPage,
      loadData: pageDataLoadingAPI.ExternalBookingPaymentPage.loadData,
    },

    {
      path: '/terms-of-service-asset',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
      loadData: pageDataLoadingAPI.TermsOfServicePage.loadData,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      path: '/piscina-con-barbacoa',
      name: 'BarbacoaPage',
      component: BarbacoaPage,
      loadData: pageDataLoadingAPI.BarbacoaPage.loadData,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    // Landings SEO por municipio y categoría (/mataro/piscinas). Va la última: cualquier ruta
    // de arriba con la misma forma gana. Las categorías van en lista blanca en el path; un
    // municipio sin esa página es el 404 de la propia página.
    {
      path: seoLandingRoutePath(),
      name: 'SeoLandingPage',
      component: SeoLandingPage,
      loadData: pageDataLoadingAPI.SeoLandingPage.loadData,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    // Do not change this path!
    //
    // The API expects that the application implements /preview endpoint
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage,
    },
  ];
};

export default routeConfiguration;
